import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FloatingCTA from '../../../components/feature/FloatingCTA';
import { faqs } from '../../../mocks/faqData';

export default function AirDuctCleaningPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const ductFaqs = faqs.filter((f) => f.category === 'airduct');

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/df3e5a54b6b7069284e01463388974ea.jpg"
            alt="Air duct cleaning Jacksonville FL - BBM Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
              <div className="h-px w-8 bg-brand-amber" />
              Air Duct Cleaning Jacksonville FL
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
              Professional Air Duct Cleaning for Healthier Jacksonville Homes
            </h1>
            <p className="text-white/80 text-xl leading-relaxed mb-7">
              NADCA-certified air duct cleaning removes dust, allergens, mold, and contaminants from your HVAC system — improving air quality, reducing energy bills, and helping your family breathe easier.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-4 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap transition-all text-center">Book Air Duct Cleaning</Link>
              <a href="tel:+19045550191" className="flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/60 text-white font-semibold py-4 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap transition-all">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill" /></div>
                (904) 555-0191
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-14 items-start">
            <div className="lg:w-3/5">
              <h2 className="font-serif text-4xl font-bold text-brand-dark mb-5">What Is Air Duct Cleaning?</h2>
              <p className="text-brand-warm-gray text-lg leading-relaxed mb-4">
                Air duct cleaning is the professional removal of dust, debris, allergens, mold spores, pet dander, and other contaminants from your home's HVAC ductwork. Over time, your duct system accumulates layers of buildup that recirculate throughout your home every time your heating or cooling system runs.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-4">
                In Jacksonville's humid climate, ductwork is especially susceptible to mold growth and moisture-related contamination. Professional air duct cleaning from BBM Services uses high-powered vacuum equipment and specialized brushing tools to thoroughly clean your entire duct system — from supply vents to returns to the air handler itself.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-8">
                After cleaning, your HVAC system operates more efficiently, your energy bills can drop, and the air circulating through your home is noticeably fresher and cleaner.
              </p>

              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-4">Benefits of Air Duct Cleaning</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: 'ri-lungs-line', title: 'Cleaner Air', desc: 'Removes dust, allergens, pet dander, and mold spores from circulation.' },
                  { icon: 'ri-heart-pulse-line', title: 'Better Health', desc: 'Significant relief for allergy, asthma, and respiratory condition sufferers.' },
                  { icon: 'ri-battery-charge-line', title: 'Energy Savings', desc: 'Clean ducts allow your HVAC to run more efficiently, lowering bills.' },
                  { icon: 'ri-timer-flash-line', title: 'Extended HVAC Life', desc: 'Reduces strain on your system, extending its operational lifespan.' },
                ].map((b) => (
                  <div key={b.title} className="flex gap-3 p-4 bg-stone-50 rounded-xl border border-stone-100">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                      <i className={`${b.icon} text-brand-amber text-lg`} />
                    </div>
                    <div>
                      <div className="font-semibold text-brand-dark text-sm mb-1">{b.title}</div>
                      <div className="text-brand-warm-gray text-xs leading-relaxed">{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-4">Signs You Need Air Duct Cleaning</h3>
              <ul className="space-y-2 mb-8">
                {['Visible dust or debris around air vents', 'Increased allergy or asthma symptoms indoors', 'Musty or stale odors when HVAC runs', 'Visible mold inside vents or on the air handler', 'Recent home renovation or construction', 'New pet in the home or heavy pet shedding', 'Haven\'t had ducts cleaned in 3+ years'].map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-brand-light">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-check-line text-brand-amber" /></div>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-2/5 space-y-5">
              <div className="bg-brand-dark rounded-2xl p-7 text-white">
                <h3 className="font-serif text-2xl font-bold mb-5">Air Duct Cleaning Pricing</h3>
                <div className="space-y-4">
                  {[
                    { service: 'Standard Residential (up to 10 vents)', price: 'From $249' },
                    { service: 'Large Home (11–20 vents)', price: 'From $349' },
                    { service: 'Duct Sanitization Treatment', price: 'From $99' },
                    { service: 'Dryer Vent Add-On', price: 'From $79' },
                  ].map((item) => (
                    <div key={item.service} className="flex items-center justify-between border-b border-white/10 pb-4 last:border-0 last:pb-0">
                      <span className="text-white/80 text-sm">{item.service}</span>
                      <span className="text-brand-amber font-bold text-sm whitespace-nowrap">{item.price}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="block bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3.5 text-center rounded-lg mt-6 cursor-pointer whitespace-nowrap transition-all text-sm">Get a Free Quote</Link>
              </div>
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
                <h4 className="font-semibold text-brand-dark mb-4">What's Included</h4>
                {['Full duct system inspection', 'High-power vacuum extraction', 'All supply & return vents cleaned', 'Air handler cleaning', 'Sanitization treatment (optional)', 'Before & after photo documentation', 'Air quality improvement report'].map((f) => (
                  <div key={f} className="flex items-center gap-2 py-2 border-b border-stone-100 last:border-0">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-check-double-line text-brand-amber text-sm" /></div>
                    <span className="text-brand-light text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-brand-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-brand-dark mb-8 text-center">Air Duct Cleaning FAQs</h2>
          <div className="space-y-3">
            {ductFaqs.map((faq) => (
              <div key={faq.id} className={`border rounded-xl overflow-hidden transition-all ${openId === faq.id ? 'border-brand-amber/40 bg-amber-50/50' : 'border-stone-200 bg-white'}`}>
                <button className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer" onClick={() => setOpenId(openId === faq.id ? null : faq.id)}>
                  <span className={`font-semibold text-sm leading-snug ${openId === faq.id ? 'text-brand-amber' : 'text-brand-dark'}`}>{faq.question}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-warm-gray'}`}>
                    <i className={`${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                  </div>
                </button>
                {openId === faq.id && <div className="px-6 pb-4"><p className="text-brand-warm-gray text-sm leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-brand-amber">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-3">Ready for Cleaner Air in Your Jacksonville Home?</h2>
          <p className="text-white/80 text-lg mb-6">NADCA-certified · Upfront pricing · Same-week availability</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-brand-amber font-bold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:bg-amber-50 transition-all">Book Air Duct Cleaning</Link>
            <a href="tel:+19045550191" className="border-2 border-white/40 text-white font-semibold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:border-white transition-all">Call (904) 555-0191</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
